/* eslint-disable @typescript-eslint/no-floating-promises */
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  SearchCountsQuery,
  SearchCountsResponse,
} from '@/api/v4/search.api';
import { fetchSearchCounts } from '@/api/v4/search.api';
import { notify } from '@/ui/snackbar/notify';
import { useTranslation } from 'react-i18next';

export type SearchCounts = SearchCountsResponse;

export const useSearchResultsCounts = (query: SearchCountsQuery) => {
  const { t } = useTranslation('default');
  const [counts, setCounts] = useState<SearchCounts | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const previousQueryRef = useRef<SearchCountsQuery | null>(null);
  const requestInProgressRef = useRef(false);

  const hasQueryChanged = useCallback(() => {
    if (!previousQueryRef.current) return true;
    const prev = previousQueryRef.current;
    return (
      prev.search !== query.search ||
      JSON.stringify(prev.entities) !== JSON.stringify(query.entities)
    );
  }, [query]);

  const performFetch = useCallback(async () => {
    if (!query.search) {
      setCounts(null);
      return;
    }
    if (requestInProgressRef.current) return;
    requestInProgressRef.current = true;
    setIsLoading(true);
    try {
      const res = await fetchSearchCounts(query);
      setCounts(res);
      setError(null);
    } catch (err) {
      setError(err as Error);
      notify({ message: t`unknownError` });
    } finally {
      setIsLoading(false);
      requestInProgressRef.current = false;
      previousQueryRef.current = { ...query };
    }
  }, [query, t]);

  useEffect(() => {
    if (hasQueryChanged()) {
      performFetch();
    }
  }, [performFetch, hasQueryChanged]);

  return { data: counts, isLoading, error };
};
